<template>
  <q-form
    ref="component"
    class="column full-height text-sm"
    autocomplete="off"
    greedy
    @validation-error="onValidationError"
  >
    <header v-if="showHeader" :class="headerClass" :style="headerStyle">
      <div>
        <div class="flex items-center">
          <span :class="headingClass">
            {{ heading }}
          </span>
          <slot name="header-cta"></slot>
        </div>
        <slot name="sub-heading">
          <span v-if="subHeading" class="text-neutral-70 text-xs">
            {{ subHeading }}
          </span>
        </slot>
      </div>
      <div class="flex items-center">
        <slot name="header-info"></slot>
        <PBtn color="primary" flat round @click="closeForm">
          <CloseIcon :class="isScreenXsOrSm ? 'icon--sm' : 'icon'" />
        </PBtn>
      </div>
    </header>
    <q-separator v-if="showHeader" color="neutral-40" />
    <div
      class="form-body col full-width overflow-y-scroll scroll-into-view"
      :class="bodyClass"
    >
      <slot></slot>
    </div>
    <q-separator v-if="isFooterVisible" />
    <footer v-if="isFooterVisible" :class="footerClass">
      <div class="flex">
        <PBtn v-if="!isEmpty(computedTernaryCta)" v-bind="computedTernaryCta" />
        <slot name="footer-info"></slot>
      </div>
      <div class="flex text-right q-ml-auto">
        <PBtn
          v-if="!isEmpty(computedQuaternaryCta)"
          v-bind="computedQuaternaryCta"
        />
        <PBtn
          v-if="!isEmpty(computedSecondaryCta)"
          v-bind="computedSecondaryCta"
          class="q-ml-sm"
        />
        <PBtn
          v-if="!isEmpty(computedPrimaryCta)"
          v-bind="computedPrimaryCta"
          class="q-ml-sm"
        />
      </div>
    </footer>
    <slot name="extra-note"></slot>

    <q-inner-loading :showing="loading" class="z-index-1">
      <q-spinner size="50px" color="primary" />
    </q-inner-loading>
  </q-form>
</template>

<script setup>
import CloseIcon from "assets/icons/close.svg";
import { isEmpty } from "assets/scripts/utilities/helpers";
import { scroll, useQuasar } from "quasar";

const { setVerticalScrollPosition } = scroll;

const component = ref(null);

const $q = useQuasar();
const screenSize = ref($q.screen.name);

const emit = defineEmits(["close"]);

defineExpose({
  component,
});

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  heading: {
    type: String,
    default: "",
  },
  headingClass: {
    type: String,
    default: "block text-lg text-neutral text-weight-medium",
  },
  subHeading: {
    type: String,
    default: "",
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  headerCta: {
    type: Object,
    default: () => ({}),
  },
  headerStyle: {
    type: Object,
    default: () => ({
      minHeight: "86px",
    }),
  },
  headerClass: {
    type: String,
    default: "row items-center justify-between no-wrap q-px-2x-md full-width",
  },
  bodyClass: {
    type: String,
    default: "q-pa-2x-md",
  },
  footerClass: {
    type: String,
    default: "q-px-2x-md q-py-lg full-width flex items-center",
  },
  primaryCta: {
    type: Object,
    default: () => {
      return {
        label: "Submit",
        type: "submit",
        color: "primary",
      };
    },
  },
  secondaryCta: {
    type: Object,
    default: () => {
      return {
        type: "reset",
        label: "Reset",
        color: "primary",
        outline: true,
      };
    },
  },
  ternaryCta: {
    type: Object,
    default: () => ({}),
  },
  quaternaryCta: {
    type: Object,
    default: () => ({}),
  },
  showFooter: {
    type: Boolean,
    default: false,
  },
});

// Use the composable for button configurations
const {
  computedPrimaryCta,
  computedSecondaryCta,
  computedTernaryCta,
  computedQuaternaryCta,
} = useFormButtons(props);

const isFooterVisible = computed(() => {
  return (
    props.showFooter ||
    !isEmpty(unref(computedPrimaryCta)) ||
    !isEmpty(unref(computedSecondaryCta)) ||
    !isEmpty(unref(computedTernaryCta)) ||
    !isEmpty(unref(computedQuaternaryCta))
  );
});

// Drawer specific functionality
const closeForm = () => {
  emit("close");
};

const { isRightDrawerOpen } = storeToRefs(useAppStore());

// Prevent unloading when drawer is open (specific to drawer functionality)
usePreventUnload({
  isPageActiveRef: isRightDrawerOpen,
});

async function onValidationError(ref) {
  const el = unref(ref).$el;
  const parentElement = el.parentElement;
  const offsetParent = el.offsetParent;

  if (!parentElement || !offsetParent) return;

  const offset = calculateOffset(el, parentElement);
  const scrollingElement = offsetParent.querySelector(".scroll-into-view");

  if (!scrollingElement) return;

  setVerticalScrollPosition(scrollingElement, offset, 500);

  await nextTick();

  handleScrollInPanel(el, scrollingElement, offset);
}

function calculateOffset(el, parentElement) {
  const offsetHeight =
    parentElement.offsetHeight <= el.offsetTop
      ? parentElement.offsetHeight
      : el.offsetHeight + 36;

  return el.offsetTop - offsetHeight - 36;
}

function findAncestorWithClass(element, className) {
  let currentElement = element;

  while (currentElement && currentElement !== document.body) {
    if (currentElement.classList.contains(...className.split(" "))) {
      return currentElement;
    }

    currentElement = currentElement.parentElement;
  }

  return null;
}

function handleScrollInPanel(el, scrollingElement, offset) {
  const qPanelScroll = findAncestorWithClass(el, "q-panel scroll");

  if (!qPanelScroll) return;

  const isParent = checkIfParent(scrollingElement, qPanelScroll);

  if (isParent) {
    setVerticalScrollPosition(qPanelScroll, offset, 500);
  }
}

function checkIfParent(childElement, parentElement) {
  let currentElement = childElement;

  while (currentElement && currentElement !== document.body) {
    if (currentElement === parentElement) {
      return true;
    }

    currentElement = currentElement.parentElement;
  }

  return false;
}

const isScreenXsOrSm = computed(
  () => screenSize.value === "xs" || screenSize.value === "sm",
);
</script>
